import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Loader2, Download, FileDown, Clock, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getCountryByCode } from '../../utils/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalytics() {
  const [timeline, setTimeline] = useState([]);
  const [responseTimes, setResponseTimes] = useState(null);
  const [heatmapPoints, setHeatmapPoints] = useState([]);
  const [demographics, setDemographics] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tl, rt, hm, dm] = await Promise.all([
        axios.get(`${API}/analytics/timeline`, { params: { days }, headers }),
        axios.get(`${API}/analytics/response-times`, { headers }),
        axios.get(`${API}/analytics/heatmap`, { headers }),
        axios.get(`${API}/analytics/passport-countries`, { headers }),
      ]);
      setTimeline(tl.data.timeline || []);
      setResponseTimes(rt.data);
      setHeatmapPoints(hm.data.points || []);
      setDemographics(dm.data);
    } catch (e) {
      toast.error('Errore caricamento analytics');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportCSV = async (tipo) => {
    try {
      const resp = await axios.get(`${API}/admin/export/${tipo}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = tipo === 'requests' ? 'richieste_lifelinke.csv' : 'utenti_lifelinke.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${tipo} scaricato`);
    } catch (e) {
      toast.error('Errore export');
    }
  };

  const maxCount = Math.max(...timeline.map(t => t.richieste), 1);

  const priorityColors = { critica: '#FF3B30', alta: '#FF9F0A', media: '#FFD60A', bassa: '#32D74B' };

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp size={16} className="text-[#FF3B30]" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Analytics e Statistiche</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-white border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="analytics-period"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={14}>Ultimi 14 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
          </select>
          <button
            onClick={() => exportCSV('requests')}
            className="flex items-center gap-1 px-3 py-2 bg-[#0A84FF] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#0070E0]"
            data-testid="export-requests-csv"
          >
            <Download size={12} /> Richieste CSV
          </button>
          <button
            onClick={() => exportCSV('users')}
            className="flex items-center gap-1 px-3 py-2 bg-[#32D74B] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#28B840]"
            data-testid="export-users-csv"
          >
            <FileDown size={12} /> Utenti CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin inline text-[#4A6B8A]" /></div>
      ) : (
        <>
          {/* Timeline chart */}
          <div className="border border-[#B8D4E8] bg-white p-5" data-testid="timeline-chart">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={14} className="text-[#4A6B8A]" />
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Richieste nel tempo ({days}gg)</h3>
            </div>
            {timeline.length === 0 ? (
              <div className="text-center py-8 text-xs text-[#6B8AB5]">Nessun dato nel periodo selezionato</div>
            ) : (
              <div className="flex items-end gap-1 h-48 border-b border-[#E8F4FD] pb-1">
                {timeline.map((t) => {
                  const h = Math.max((t.richieste / maxCount) * 100, 2);
                  return (
                    <div key={t.data} className="flex-1 flex flex-col items-center justify-end gap-1 group">
                      <div
                        className="w-full bg-[#FF3B30] hover:bg-[#FF1A1A] transition-all relative"
                        style={{ height: `${h}%` }}
                        data-testid={`bar-${t.data}`}
                      >
                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-[#0D1B2A]">{t.richieste}</span>
                      </div>
                      <span className="text-[8px] text-[#6B8AB5] font-mono">{t.data.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Response times / stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[#B8D4E8] bg-white p-5" data-testid="categories-stats">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-4">Per Categoria</h3>
              {Object.entries(responseTimes?.per_categoria || {}).length === 0 ? (
                <div className="text-xs text-[#6B8AB5]">Nessun dato</div>
              ) : (
                Object.entries(responseTimes?.per_categoria || {}).map(([cat, count]) => {
                  const total = Object.values(responseTimes.per_categoria).reduce((a, b) => a + b, 0);
                  const pct = total ? (count / total) * 100 : 0;
                  return (
                    <div key={cat} className="py-1.5">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[11px] text-[#0D1B2A] uppercase">{cat?.replace(/_/g, ' ')}</span>
                        <span className="text-[11px] font-mono text-[#FF3B30]">{count}</span>
                      </div>
                      <div className="h-1.5 bg-[#E8F4FD]">
                        <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border border-[#B8D4E8] bg-white p-5" data-testid="priority-stats">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-4">Risolte per Priorità</h3>
              {Object.entries(responseTimes?.risolte_per_priorita || {}).map(([prio, count]) => (
                <div key={prio} className="flex items-center justify-between py-2 border-b border-[#E8F4FD] last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2" style={{ backgroundColor: priorityColors[prio] || '#6B8AB5' }} />
                    <span className="text-sm text-[#0D1B2A] uppercase font-bold">{prio}</span>
                  </div>
                  <span className="text-sm font-mono" style={{ color: priorityColors[prio] || '#0D1B2A' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap summary */}
          <div className="border border-[#B8D4E8] bg-white p-5" data-testid="heatmap-summary">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-4">Distribuzione Geografica ({heatmapPoints.length} punti)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['critica', 'alta', 'media', 'bassa'].map((p) => {
                const count = heatmapPoints.filter(h => h.priorita === p).length;
                return (
                  <div key={p} className="border border-[#E8F4FD] p-3">
                    <div className="text-[10px] uppercase text-[#4A6B8A] tracking-widest">{p}</div>
                    <div className="text-2xl font-mono font-bold" style={{ color: priorityColors[p] }}>{count}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Demographics: id type + passport countries */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-[#B8D4E8] bg-white p-5" data-testid="demographics-types">
              <div className="flex items-center gap-2 mb-4">
                <Globe size={14} className="text-[#4A6B8A]" />
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Tipo Identificativo</h3>
              </div>
              {demographics?.per_tipo_identificativo ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-[#E8F4FD]">
                    <span className="text-xs text-[#0D1B2A]">🇮🇹 Codice Fiscale</span>
                    <span className="text-sm font-mono font-bold text-[#FF3B30]">{demographics.per_tipo_identificativo.italia_cf}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-[#E8F4FD]">
                    <span className="text-xs text-[#0D1B2A]">🇸🇲 Codice ISS</span>
                    <span className="text-sm font-mono font-bold text-[#0A84FF]">{demographics.per_tipo_identificativo.san_marino_iss}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-[#0D1B2A]">📔 Passaporto</span>
                    <span className="text-sm font-mono font-bold text-[#FF9F0A]">{demographics.per_tipo_identificativo.estero_passaporto}</span>
                  </div>
                </div>
              ) : <div className="text-xs text-[#6B8AB5]">Nessun dato</div>}
            </div>

            <div className="md:col-span-2 border border-[#B8D4E8] bg-white p-5" data-testid="demographics-countries">
              <div className="flex items-center gap-2 mb-4">
                <Globe size={14} className="text-[#4A6B8A]" />
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">
                  Paesi di origine ({demographics?.totale_passaporti || 0} utenti con passaporto)
                </h3>
              </div>
              {demographics?.per_paese?.length === 0 || !demographics?.per_paese ? (
                <div className="text-xs text-[#6B8AB5]">Nessun utente con passaporto registrato</div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {demographics.per_paese.map((p) => {
                    const c = getCountryByCode(p.code);
                    const total = demographics.totale_passaporti || 1;
                    const pct = (p.count / total) * 100;
                    return (
                      <div key={p.code} className="border border-[#E8F4FD] p-2" data-testid={`country-${p.code}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#0D1B2A]">
                            {c ? <>{c.flag} {c.name}</> : `[${p.code}]`}
                          </span>
                          <span className="text-xs font-mono font-bold text-[#FF3B30]">{p.count}</span>
                        </div>
                        <div className="h-1 bg-[#E8F4FD]">
                          <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
